// src/components/manager-v2/TeamAttendanceManagement.js
// Team Attendance Management for Managers - shows only reporting employees
// Adapted from ComprehensiveAttendanceManagement.js with team-only filtering

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Calendar, Users, Clock, Download, CheckCircle, XCircle,
    AlertCircle, Filter, Home, X, PartyPopper, ChevronUp
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import attendanceService from '../../services/attendance.service';
import holidayService from '../../services/holiday.service';
import { api } from '../../services/api';
import { toISTDateString, getCurrentISTDate, parseISTDate, formatTime } from '../../utils/dateHelpers';
import AttendanceCalendarEnhanced from '../admin/attendance/AttendanceCalendarEnhanced';

const TeamAttendanceManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();

    // Initialize with IST date to avoid timezone issues
    const getLocalDateString = () => {
        return getCurrentISTDate();
    };

    // Helper function to parse weekly off string into day numbers
    const parseWeeklyOff = (weeklyOffStr) => {
        if (!weeklyOffStr) return [];

        const weeklyOffStr_lower = String(weeklyOffStr).toLowerCase();
        const dayMap = {
            'sunday': 0, 'sundays': 0, 'sun': 0,
            'monday': 1, 'mondays': 1, 'mon': 1,
            'tuesday': 2, 'tuesdays': 2, 'tue': 2, 'tues': 2,
            'wednesday': 3, 'wednesdays': 3, 'wed': 3,
            'thursday': 4, 'thursdays': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
            'friday': 5, 'fridays': 5, 'fri': 5,
            'saturday': 6, 'saturdays': 6, 'sat': 6
        };

        // Remove common non-day words (only, off, day, days)
        const cleanedStr = weeklyOffStr_lower
            .replace(/\b(only|off|day|days)\b/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        // Split by comma, ampersand, "and", or spaces
        const parts = cleanedStr.split(/[,&]|\band\b|\s+/);
        const days = [];

        parts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed && dayMap.hasOwnProperty(trimmed)) {
                const dayNum = dayMap[trimmed];
                if (!days.includes(dayNum)) {
                    days.push(dayNum);
                }
            }
        });

        return days;
    };

    // Check if date is week-off for employee
    const isWeekOff = (date, employee) => {
        if (!employee || !employee.weekly_off) return false;
        const weeklyOffDays = parseWeeklyOff(employee.weekly_off);
        const dayOfWeek = date.getDay();
        return weeklyOffDays.includes(dayOfWeek);
    };

    // Get normalized employee ID
    const getEmployeeId = (record) => {
        return record.employee_id || record.id || record.emp_id;
    };

    // Status priority for conflict resolution (higher number = higher priority)
    const STATUS_PRIORITY = {
        'inactive': 7,
        'leave': 6,
        'holiday': 5,
        'week_off': 4,
        'present': 3,
        'half-day': 2,
        'absent': 1
    };

    const [selectedDate, setSelectedDate] = useState(getLocalDateString());
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterEmployee, setFilterEmployee] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all');
    const [stats, setStats] = useState({
        total: 0,
        present: 0,
        absent: 0,
        leave: 0,
        holiday: 0,
        weekOff: 0,
        inactive: 0,
        wfh: 0,
        office: 0
    });

    // Calendar widget state
    const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
    const [monthlyCache, setMonthlyCache] = useState({});
    const [calendarCache, setCalendarCache] = useState(null);
    const detailsRef = useRef(null);

    // Handler to receive monthly data from calendar
    const handleMonthDataLoaded = (monthData) => {
        setMonthlyCache(monthData);
        setCalendarCache(monthData);
    };

    // Get team members - employees who report to this manager
    const teamMembers = useMemo(() => {
        if (!user?.id || !employees) return [];
        return employees.filter(emp =>
            emp.managerId === user.id || emp.reporting_manager_id === user.id
        );
    }, [user?.id, employees]);

    const selectedDateObj = parseISTDate(selectedDate) || new Date();

    // Fetch team attendance data
    useEffect(() => {
        if (teamMembers.length > 0) {
            fetchTeamAttendanceData();
        } else {
            setLoading(false);
            setAttendanceData([]);
            setStats({
                total: 0,
                present: 0,
                absent: 0,
                leave: 0,
                holiday: 0,
                weekOff: 0,
                inactive: 0,
                wfh: 0,
                office: 0
            });
        }
    }, [selectedDate, teamMembers]);

    const fetchTeamAttendanceData = async () => {
        try {
            setLoading(true);

            // Fetch team attendance and leave data in parallel
            const [attendanceResponse, leaveResponse, holidayResponse] = await Promise.all([
                attendanceService.getTeamAttendance(selectedDate),
                api.get('/api/leaves/all-requests'),
                holidayService.isHoliday(selectedDate)
            ]);

            const attendanceRecords = attendanceResponse.data || [];
            const allLeaveData = leaveResponse.data.leaves || leaveResponse.data.requests || leaveResponse.data || [];
            const isHoliday = holidayResponse.isHoliday || false;

            // Create team member IDs set for filtering
            const teamMemberIds = new Set(teamMembers.map(emp => emp.id));

            // Create employee status map for deduplication
            const employeeStatusMap = new Map();
            const selectedDay = parseISTDate(selectedDate);
            const selectedDateStr = toISTDateString(selectedDate);

            // Step 1: Initialize all team members with default status
            teamMembers.forEach(emp => {
                const empId = getEmployeeId(emp);
                const joiningDateStr = emp.joining_date ? toISTDateString(emp.joining_date) : null;

                // Determine initial status based on conditions
                let initialStatus = 'absent';
                if (joiningDateStr && selectedDateStr < joiningDateStr) {
                    initialStatus = 'inactive';
                } else if (isHoliday) {
                    initialStatus = 'holiday';
                } else if (isWeekOff(selectedDay, emp)) {
                    initialStatus = 'week_off';
                }

                employeeStatusMap.set(empId, {
                    employee_id: empId,
                    id: empId,
                    employee_code: emp.employee_code,
                    first_name: emp.first_name,
                    last_name: emp.last_name,
                    department_name: emp.department_name || emp.department,
                    status: initialStatus,
                    joining_date: emp.joining_date,
                    weekly_off: emp.weekly_off,
                    check_in_time: null,
                    check_out_time: null,
                    work_hours: 0,
                    work_location: null
                });
            });

            // Step 2: Process attendance data (present employees)
            attendanceRecords.forEach(record => {
                const empId = getEmployeeId(record);

                // Only process if this employee is in the team
                if (!teamMemberIds.has(empId)) return;

                const existing = employeeStatusMap.get(empId);

                if (existing && existing.status !== 'inactive' && existing.status !== 'leave') {
                    employeeStatusMap.set(empId, {
                        ...existing,
                        ...record,
                        status: 'present',
                        employee_code: record.employee_code || existing.employee_code,
                        first_name: record.first_name || existing.first_name,
                        last_name: record.last_name || existing.last_name,
                        department_name: record.department_name || existing.department_name
                    });
                }
            });

            // Step 3: Process leave data (approved leaves for team members only)
            const approvedLeaves = allLeaveData.filter(leave => {
                if (leave.status !== 'approved') return false;

                const empId = getEmployeeId(leave);
                if (!teamMemberIds.has(empId)) return false;

                const leaveStart = new Date(leave.from_date || leave.start_date);
                const leaveEnd = new Date(leave.to_date || leave.end_date);
                leaveStart.setHours(0, 0, 0, 0);
                leaveEnd.setHours(23, 59, 59, 999);

                return selectedDay >= leaveStart && selectedDay <= leaveEnd;
            });

            approvedLeaves.forEach(leave => {
                const empId = getEmployeeId(leave);
                const existing = employeeStatusMap.get(empId);

                if (existing && existing.status !== 'inactive') {
                    const nameParts = (leave.employee_name || '').split(' ');
                    const firstName = leave.first_name || nameParts[0] || existing.first_name;
                    const lastName = leave.last_name || nameParts.slice(1).join(' ') || existing.last_name;

                    employeeStatusMap.set(empId, {
                        ...existing,
                        status: 'leave',
                        leave_type: leave.leave_type_name || leave.leave_type,
                        leave_reason: leave.reason,
                        first_name: firstName,
                        last_name: lastName,
                        employee_code: leave.employee_code || existing.employee_code,
                        department_name: leave.department_name || existing.department_name,
                        check_in_time: null,
                        check_out_time: null,
                        work_hours: 0
                    });
                }
            });

            // Convert map to array
            const completeAttendanceData = Array.from(employeeStatusMap.values());

            // Calculate stats
            const newStats = {
                total: 0,
                present: 0,
                absent: 0,
                leave: 0,
                holiday: 0,
                weekOff: 0,
                inactive: 0,
                wfh: 0,
                office: 0
            };

            completeAttendanceData.forEach(emp => {
                if (emp.status === 'inactive') {
                    newStats.inactive++;
                } else {
                    newStats.total++;

                    switch (emp.status) {
                        case 'present':
                            newStats.present++;
                            const locationFromWfh = (emp.work_location_from_wfh || '').toLowerCase();
                            const locationFromPunch = (emp.work_location || '').toLowerCase();
                            const locationFromRegularization = (emp.location?.type || '').toLowerCase();

                            const isWFH = locationFromWfh.includes('wfh') ||
                                locationFromPunch.includes('wfh') ||
                                locationFromPunch.includes('remote') ||
                                locationFromPunch.includes('home') ||
                                locationFromRegularization.includes('wfh');

                            if (isWFH) {
                                newStats.wfh++;
                            } else {
                                newStats.office++;
                            }
                            break;
                        case 'absent':
                            newStats.absent++;
                            break;
                        case 'leave':
                            newStats.leave++;
                            break;
                        case 'holiday':
                            newStats.holiday++;
                            break;
                        case 'week_off':
                            newStats.weekOff++;
                            break;
                    }
                }
            });

            setAttendanceData(completeAttendanceData);
            setStats(newStats);
        } catch (error) {
            console.error('Error fetching team attendance:', error);
            setAttendanceData([]);
            setStats({
                total: 0,
                present: 0,
                absent: 0,
                leave: 0,
                holiday: 0,
                weekOff: 0,
                inactive: 0,
                wfh: 0,
                office: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // Get unique departments for filter dropdown
    const uniqueDepartments = useMemo(() => {
        const departments = new Set();
        attendanceData.forEach(record => {
            if (record.department_name) {
                departments.add(record.department_name);
            }
        });
        return Array.from(departments).sort();
    }, [attendanceData]);

    // Filtered attendance data
    const filteredAttendanceData = useMemo(() => {
        return attendanceData.filter(record => {
            // Filter by status
            if (filterStatus !== 'all' && record.status !== filterStatus) {
                return false;
            }

            // Filter by employee name
            if (filterEmployee) {
                const fullName = `${record.first_name} ${record.last_name}`.toLowerCase();
                const employeeCode = (record.employee_code || '').toLowerCase();
                const searchTerm = filterEmployee.toLowerCase();
                if (!fullName.includes(searchTerm) && !employeeCode.includes(searchTerm)) {
                    return false;
                }
            }

            // Filter by department
            if (filterDepartment !== 'all' && record.department_name !== filterDepartment) {
                return false;
            }

            // Filter by location (WFH/Office)
            if (filterLocation !== 'all' && record.status === 'present') {
                const locationFromWfh = (record.work_location_from_wfh || '').toLowerCase();
                const locationFromPunch = (record.work_location || '').toLowerCase();
                const locationFromRegularization = (record.location?.type || '').toLowerCase();

                const isWFH = locationFromWfh.includes('wfh') ||
                    locationFromPunch.includes('wfh') ||
                    locationFromPunch.includes('remote') ||
                    locationFromPunch.includes('home') ||
                    locationFromRegularization.includes('wfh');

                if (filterLocation === 'wfh' && !isWFH) {
                    return false;
                }
                if (filterLocation === 'office' && isWFH) {
                    return false;
                }
            }

            return true;
        });
    }, [attendanceData, filterStatus, filterEmployee, filterDepartment, filterLocation]);

    const getStatusBadge = (status) => {
        const config = {
            present: {
                bg: 'bg-green-100 dark:bg-green-900/30',
                text: 'text-green-700 dark:text-green-400',
                icon: CheckCircle,
                label: 'Present'
            },
            absent: {
                bg: 'bg-red-100 dark:bg-red-900/30',
                text: 'text-red-700 dark:text-red-400',
                icon: XCircle,
                label: 'Absent'
            },
            'half-day': {
                bg: 'bg-yellow-100 dark:bg-yellow-900/30',
                text: 'text-yellow-700 dark:text-yellow-400',
                icon: AlertCircle,
                label: 'Half Day'
            },
            leave: {
                bg: 'bg-blue-100 dark:bg-blue-900/30',
                text: 'text-blue-700 dark:text-blue-400',
                icon: Clock,
                label: 'On Leave'
            },
            holiday: {
                bg: 'bg-orange-100 dark:bg-orange-900/30',
                text: 'text-orange-700 dark:text-orange-400',
                icon: PartyPopper,
                label: 'Holiday'
            },
            week_off: {
                bg: 'bg-purple-100 dark:bg-purple-900/30',
                text: 'text-purple-700 dark:text-purple-400',
                icon: Home,
                label: 'Week Off'
            },
            inactive: {
                bg: 'bg-gray-100 dark:bg-gray-700',
                text: 'text-gray-600 dark:text-gray-400',
                icon: Users,
                label: 'Inactive'
            }
        };

        const cfg = config[status] || config.absent;
        const Icon = cfg.icon;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
                <Icon className="w-3 h-3 mr-1" />
                {cfg.label}
            </span>
        );
    };

    const exportAttendance = () => {
        if (attendanceData.length === 0) {
            alert('No attendance data to export');
            return;
        }

        const csvData = filteredAttendanceData.map(record => ({
            'Employee Code': record.employee_code || '-',
            'Name': `${record.first_name} ${record.last_name}`,
            'Department': record.department_name || '-',
            'Check In': record.check_in_time ? formatTime(record.check_in_time) : '-',
            'Check Out': record.check_out_time ? formatTime(record.check_out_time) : '-',
            'Hours': record.work_hours ? `${record.work_hours}h` : '0h',
            'Status': record.status || 'absent'
        }));

        const csv = [
            Object.keys(csvData[0]).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `team_attendance_${selectedDate}.csv`;
        a.click();
    };

    // Handler to open employee profile
    const handleEmployeeClick = (employeeId) => {
        navigate(`/manager/team-management/${employeeId}`);
    };

    // Empty state when no team members
    if (!loading && teamMembers.length === 0) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Attendance</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Monitor and track your team's attendance
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                    <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Team Members Found</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        You don't have any team members assigned to you yet.
                    </p>
                    <button
                        onClick={() => window.location.href = 'mailto:hr@company.com?subject=Team Assignment Request'}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Contact HR for Team Assignment
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Team Attendance</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Track your team's attendance and work hours
                    </p>
                </div>
            </div>

            {/* Calendar Widget - Expandable */}
            {isCalendarExpanded && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                    <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Date</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Click a date to view team attendance</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsCalendarExpanded(false)}
                            className="relative p-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 group animate-pulse hover:animate-none"
                            title="Minimize Calendar"
                        >
                            <span className="absolute inset-0 rounded-lg bg-red-400 dark:bg-red-500 animate-ping opacity-30 group-hover:opacity-0"></span>
                            <ChevronUp className="relative w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                        </button>
                    </div>
                    <div className="p-4">
                        <AttendanceCalendarEnhanced
                            selectedDate={selectedDate}
                            onDateSelect={(date) => {
                                setSelectedDate(date);
                                setTimeout(() => {
                                    detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                            }}
                            totalEmployees={teamMembers.length}
                            allEmployees={teamMembers}
                            onMonthDataLoaded={handleMonthDataLoaded}
                            cachedData={calendarCache}
                        />
                    </div>
                </div>
            )}

            {/* Date Info & Stats */}
            <div ref={detailsRef} className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                            className={`relative p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all group ${
                                !isCalendarExpanded ? 'animate-pulse' : ''
                            }`}
                            title={isCalendarExpanded ? "Hide Calendar" : "Show Calendar"}
                        >
                            {!isCalendarExpanded && (
                                <span className="absolute inset-0 rounded-lg bg-blue-400 dark:bg-blue-500 animate-ping opacity-20"></span>
                            )}
                            {isCalendarExpanded ? (
                                <ChevronUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            ) : (
                                <Calendar className="relative w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:scale-125 transition-transform" />
                            )}
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {new Date(selectedDate).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Click calendar icon to change date
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={exportAttendance}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Total Team</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {stats.present}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Present</div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Office: {stats.office} | WFH: {stats.wfh}
                        </div>
                    </div>
                    <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {stats.absent}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Absent</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {stats.leave}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">On Leave</div>
                    </div>
                </div>
            </div>

            {/* Employee Attendance Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                <div className="p-4 border-b dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            Team Attendance Details
                        </h3>
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Filter:</span>
                        </div>
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                filterStatus === 'all'
                                    ? 'bg-gray-600 text-white dark:bg-gray-500'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            All ({stats.total})
                        </button>
                        <button
                            onClick={() => setFilterStatus('present')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                filterStatus === 'present'
                                    ? 'bg-green-600 text-white dark:bg-green-500'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30'
                            }`}
                        >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Present ({stats.present})
                        </button>
                        <button
                            onClick={() => setFilterStatus('absent')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                filterStatus === 'absent'
                                    ? 'bg-red-600 text-white dark:bg-red-500'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30'
                            }`}
                        >
                            <XCircle className="w-3.5 h-3.5" />
                            Absent ({stats.absent})
                        </button>
                        <button
                            onClick={() => setFilterStatus('leave')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                filterStatus === 'leave'
                                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/30'
                            }`}
                        >
                            <Clock className="w-3.5 h-3.5" />
                            On Leave ({stats.leave})
                        </button>
                        <button
                            onClick={() => setFilterStatus('holiday')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                filterStatus === 'holiday'
                                    ? 'bg-orange-600 text-white dark:bg-orange-500'
                                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/30'
                            }`}
                        >
                            <PartyPopper className="w-3.5 h-3.5" />
                            Holiday ({stats.holiday || 0})
                        </button>
                        <button
                            onClick={() => setFilterStatus('week_off')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                                filterStatus === 'week_off'
                                    ? 'bg-purple-600 text-white dark:bg-purple-500'
                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/30'
                            }`}
                        >
                            <Home className="w-3.5 h-3.5" />
                            Week Off ({stats.weekOff || 0})
                        </button>

                        {/* Clear Filters Button */}
                        {(filterEmployee || filterDepartment !== 'all' || filterLocation !== 'all' || filterStatus !== 'all') && (
                            <button
                                onClick={() => {
                                    setFilterEmployee('');
                                    setFilterDepartment('all');
                                    setFilterLocation('all');
                                    setFilterStatus('all');
                                }}
                                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-all flex items-center gap-1"
                            >
                                <X className="w-3.5 h-3.5" />
                                Clear All Filters
                            </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <div className="max-h-[600px] overflow-y-auto">
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="text-gray-600 dark:text-gray-400">Loading team attendance...</div>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                            Employee
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                            Department
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                            Location
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Check In</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Check Out</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Hours</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                            Status
                                        </th>
                                    </tr>
                                    {/* Filter Row */}
                                    <tr className="bg-gray-100 dark:bg-gray-600">
                                        <th className="px-4 py-2">
                                            <input
                                                type="text"
                                                placeholder="Search employee..."
                                                value={filterEmployee}
                                                onChange={(e) => setFilterEmployee(e.target.value)}
                                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </th>
                                        <th className="px-4 py-2">
                                            <select
                                                value={filterDepartment}
                                                onChange={(e) => setFilterDepartment(e.target.value)}
                                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="all">All Departments</option>
                                                {uniqueDepartments.map(dept => (
                                                    <option key={dept} value={dept}>{dept}</option>
                                                ))}
                                            </select>
                                        </th>
                                        <th className="px-4 py-2">
                                            <select
                                                value={filterLocation}
                                                onChange={(e) => setFilterLocation(e.target.value)}
                                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="all">All</option>
                                                <option value="wfh">WFH</option>
                                                <option value="office">Office</option>
                                            </select>
                                        </th>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2">
                                            <select
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="all">All Status</option>
                                                <option value="present">Present</option>
                                                <option value="absent">Absent</option>
                                                <option value="leave">Leave</option>
                                                <option value="holiday">Holiday</option>
                                                <option value="week_off">Week Off</option>
                                            </select>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y dark:divide-gray-700">
                                    {filteredAttendanceData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                                {filterStatus === 'all'
                                                    ? 'No attendance records found for this date'
                                                    : `No ${filterStatus} employees found`
                                                }
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredAttendanceData.map(record => (
                                            <tr
                                                key={record.employee_id}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                                onClick={() => handleEmployeeClick(record.employee_id)}
                                                title="Click to view employee profile"
                                            >
                                                <td className="px-4 py-3">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {record.first_name} {record.last_name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{record.employee_code}</p>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {record.department_name || '-'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {record.status === 'present' ? (
                                                        (() => {
                                                            const locationFromWfh = (record.work_location_from_wfh || '').toLowerCase();
                                                            const locationFromPunch = (record.work_location || '').toLowerCase();
                                                            const locationFromRegularization = (record.location?.type || '').toLowerCase();

                                                            const isWFH = locationFromWfh.includes('wfh') ||
                                                                locationFromPunch.includes('wfh') ||
                                                                locationFromPunch.includes('remote') ||
                                                                locationFromPunch.includes('home') ||
                                                                locationFromRegularization.includes('wfh');

                                                            return isWFH ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                                                                    <Home className="w-3 h-3 mr-1" />
                                                                    WFH
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                    <Users className="w-3 h-3 mr-1" />
                                                                    Office
                                                                </span>
                                                            );
                                                        })()
                                                    ) : (
                                                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {record.check_in_time ? formatTime(record.check_in_time) : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {record.check_out_time ? formatTime(record.check_out_time) : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                    {record.work_hours ? `${record.work_hours}h` : '0h'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {getStatusBadge(record.status || 'absent')}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamAttendanceManagement;
